import { api } from '../api/client';
import type { 
  Collection, 
  CollectionWithItems, 
  CollectionCreate, 
  CollectionUpdate,
  CollectionItem,
  CollectionItemCreate,
  CollectionItemUpdate
} from '../types/collections';

// One collection's accumulation signals, from `GET /{id}/health`.
export interface DuplicateCluster {
  name_key: string;       // normalized label the rows share, e.g. "alex"
  cluster_key: string;    // sha256 of the sorted ids — names the GROUP, for dismissal
  label: string;          // how the group renders, for the badge/list
  item_ids: number[];     // always length >= 2
}

export interface CollectionHealth {
  collection_id: number;
  item_count: number;
  orphan_count: number;
  duplicate_clusters: DuplicateCluster[];
  // Clusters ruled "different people". Out of the badge, still returned, so a
  // misclicked Dismiss can be undone.
  dismissed_clusters: DuplicateCluster[];
}

// Collections API
export const collectionsApi = {
  // List all collections
  list: async (): Promise<Collection[]> => {
    const response = await api.get('/api/collections/');
    return response.data;
  },

  // Get a specific collection
  get: async (collectionId: number, includeItems = false): Promise<CollectionWithItems> => {
    const response = await api.get(`/api/collections/${collectionId}?include_items=${includeItems}`);
    return response.data;
  },

  // Create a new collection
  create: async (data: CollectionCreate): Promise<Collection> => {
    const response = await api.post('/api/collections/', data);
    return response.data;
  },

  // Update a collection
  update: async (collectionId: number, data: CollectionUpdate): Promise<Collection> => {
    const response = await api.put(`/api/collections/${collectionId}`, data);
    return response.data;
  },

  // Delete a collection
  delete: async (collectionId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/collections/${collectionId}`);
    return response.data;
  },

  // Collection Items API
  items: {
    // List items in a collection
    list: async (collectionId: number, limit = 100, offset = 0): Promise<CollectionItem[]> => {
      const response = await api.get(`/api/collections/${collectionId}/items?limit=${limit}&offset=${offset}`);
      return response.data;
    },

    // Full-text search items within a single collection (server-side, across
    // all data fields). Unlike `list`, this is not capped to a page of recent
    // items, so the checkin reference picker can find any matching item.
    search: async (collectionId: number, query: string, limit = 25): Promise<CollectionItem[]> => {
      const response = await api.get('/api/collections/items/search', {
        params: { query, collection_id: collectionId, limit }
      });
      return response.data;
    },

    // Get a specific item
    get: async (collectionId: number, itemId: number): Promise<CollectionItem> => {
      const response = await api.get(`/api/collections/${collectionId}/items/${itemId}`);
      return response.data;
    },

    // Create a new item
    create: async (collectionId: number, data: Omit<CollectionItemCreate, 'collection_id'>): Promise<CollectionItem> => {
      const response = await api.post(`/api/collections/${collectionId}/items`, {
        ...data,
        collection_id: collectionId
      });
      return response.data;
    },

    // Update an item
    update: async (collectionId: number, itemId: number, data: CollectionItemUpdate): Promise<CollectionItem> => {
      const response = await api.put(`/api/collections/${collectionId}/items/${itemId}`, data);
      return response.data;
    },

    // Delete an item
    delete: async (collectionId: number, itemId: number, force: boolean = false): Promise<{ message: string; can_delete?: boolean; reference_count?: number }> => {
      const response = await api.delete(`/api/collections/${collectionId}/items/${itemId}${force ? '?force=true' : ''}`);
      return response.data;
    },

    // What is accumulating in this collection that the grid cannot show:
    // `orphan_count` (rows nothing live references — what Prune would offer)
    // and `duplicate_clusters` (groups of rows that render as the same label,
    // each carrying its ids so a caller can filter its grid to them).
    //
    // Cheap on purpose: the server computes no per-item reference count, which
    // is the expensive half. Use `referenceCounts` when you need per-row
    // numbers to CHOOSE with; use this to learn whether there is anything to
    // choose about.
    health: async (collectionId: number): Promise<CollectionHealth> => {
      const response = await api.get(`/api/collections/${collectionId}/health`);
      return response.data;
    },

    // Record "these are different people" for one cluster: it leaves the
    // badge and the review list, and lands in `dismissed_clusters`. Keyed
    // server-side on the exact id set, so a later third row of the same name
    // reopens the question rather than inheriting the verdict. Idempotent.
    dismissDuplicates: async (
      collectionId: number,
      itemIds: number[]
    ): Promise<{ dismissed: boolean; item_ids: number[]; cluster_key: string }> => {
      const response = await api.post(`/api/collections/${collectionId}/duplicates/dismiss`, {
        item_ids: itemIds,
      });
      return response.data;
    },

    // Put a dismissed cluster back in the review list.
    undismissDuplicates: async (
      collectionId: number,
      clusterKey: string
    ): Promise<{ restored: boolean }> => {
      const response = await api.delete(
        `/api/collections/${collectionId}/duplicates/dismiss/${clusterKey}`
      );
      return response.data;
    },

    // Batch "appears in N check-ins" counts for the merge/de-dupe UI. Returns
    // a map of item id -> number of DISTINCT check-ins referencing it via
    // either reference column (scalar/list/map-value or map-key), excluding
    // deleted tags. Every requested id is present in the map (unreferenced =>
    // 0). JSON object keys arrive as strings, so they're normalized to numbers.
    referenceCounts: async (collectionId: number, itemIds: number[]): Promise<Record<number, number>> => {
      const response = await api.post(`/api/collections/${collectionId}/reference_counts`, {
        item_ids: itemIds,
      });
      const raw = (response.data ?? {}) as Record<string, number>;
      const out: Record<number, number> = {};
      for (const [k, v] of Object.entries(raw)) {
        out[Number(k)] = v;
      }
      return out;
    },

    // Ask the LLM to synthesize one merged entity from the selected candidate
    // items (2..10, all in this collection). READ-ONLY: nothing is written;
    // returns the proposed merged field map plus a recommended survivor id.
    // The review modal edits `proposed_data` and posts it back via `mergeCommit`.
    mergePropose: async (
      collectionId: number,
      itemIds: number[]
    ): Promise<{ recommended_survivor_id: number; proposed_data: Record<string, any> }> => {
      const response = await api.post(`/api/collections/${collectionId}/merge/propose`, {
        item_ids: itemIds,
      });
      return response.data;
    },

    // Commit an approved merge: writes `data` onto the survivor (reusing its
    // id), re-points every check-in reference from the losers onto the
    // survivor, and reaps the losers — all atomically. `itemIds` is the full
    // selected set (survivor + losers); `survivorId` must be one of them.
    // Returns the surviving item plus a re-point summary.
    mergeCommit: async (
      collectionId: number,
      survivorId: number,
      data: Record<string, any>,
      itemIds: number[]
    ): Promise<{ survivor: CollectionItem; repointed_checkin_count: number; reaped_loser_ids: number[] }> => {
      const response = await api.post(`/api/collections/${collectionId}/merge/commit`, {
        survivor_id: survivorId,
        data,
        item_ids: itemIds,
      });
      return response.data;
    },

    // Hard-delete the still-unreferenced subset of `itemIds` from this
    // collection. The server recomputes which of the passed ids actually have
    // zero LIVE references (the client list is only a hint), FK-safely clears
    // any `manual_override='delete'` tombstone tags, then reaps them. Returns
    // the ids actually removed (`reaped_ids`) and those skipped because they
    // gained a live reference since listing (`skipped_ids`).
    purgeUnreferenced: async (
      collectionId: number,
      itemIds: number[]
    ): Promise<{ reaped_ids: number[]; skipped_ids: number[] }> => {
      const response = await api.post(`/api/collections/${collectionId}/purge_unreferenced`, {
        item_ids: itemIds,
      });
      return response.data;
    },
  },
};